import ClearIcon from '@mui/icons-material/Clear';
import InboxIcon from '@mui/icons-material/Inbox';
import RateReviewIcon from '@mui/icons-material/RateReview';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type EvalRun, useListRunsQuery } from './api/evalApi';

type QueueMode = 'needs_review' | 'regressions';

const STATUS_COLOR_MAP: Record<EvalRun['status'], 'default' | 'info' | 'success' | 'error'> = {
  pending: 'default',
  running: 'info',
  completed: 'success',
  failed: 'error',
};

function formatTimeAgo(dateString: string): string {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const diffSeconds = Math.floor((now - then) / 1000);

  if (diffSeconds < 60) return 'just now';
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function truncateId(id: string, maxLength = 12): string {
  return id.length > maxLength ? `${id.slice(0, maxLength)}...` : id;
}

function RunCardSkeleton() {
  return (
    <Card variant="outlined">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
        <Skeleton variant="rounded" width={72} height={24} />
        <Skeleton variant="text" width={160} height={24} />
        <Box sx={{ flex: 1 }} />
        <Skeleton variant="text" width={80} height={20} />
        <Skeleton variant="text" width={48} height={20} />
        <Skeleton variant="rounded" width={88} height={24} />
      </Box>
    </Card>
  );
}

interface RunCardProps {
  run: EvalRun;
  onNavigate: (id: string) => void;
}

function RunCard({ run, onNavigate }: RunCardProps) {
  return (
    <Card variant="outlined">
      <CardActionArea onClick={() => onNavigate(run.id)}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2,
            py: 1.5,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
          }}
        >
          {/* Left section: status + provider:model */}
          <Chip
            label={run.status}
            size="small"
            color={STATUS_COLOR_MAP[run.status]}
            sx={{ fontWeight: 600, textTransform: 'capitalize', minWidth: 72 }}
          />
          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
            {run.provider}:{run.model}
          </Typography>

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Right section: case ID, time, action chips */}
          <Tooltip title={run.case_id} placement="top">
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: 'monospace' }}
              noWrap
            >
              {truncateId(run.case_id)}
            </Typography>
          </Tooltip>
          <Typography variant="caption" color="text.secondary" noWrap>
            {formatTimeAgo(run.created_at)}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            {run.requires_review && (
              <Tooltip title="A human should verify this result">
                <Chip label="Needs Review" size="small" color="warning" />
              </Tooltip>
            )}
            {run.is_regression && (
              <Tooltip title="Output quality dropped since last run">
                <Chip label="Regression" size="small" color="error" />
              </Tooltip>
            )}
          </Stack>
        </Box>
      </CardActionArea>
    </Card>
  );
}

interface EmptyStateProps {
  mode: QueueMode;
}

function EmptyState({ mode }: EmptyStateProps) {
  const isReview = mode === 'needs_review';
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        color: 'text.secondary',
      }}
    >
      <InboxIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
      <Typography variant="h6" gutterBottom>
        Queue is empty
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {isReview
          ? 'No runs are awaiting human review right now.'
          : 'No regressions have been detected in recent runs.'}
      </Typography>
    </Box>
  );
}

export default function EvalReviewQueuePage() {
  const navigate = useNavigate();
  const [queueMode, setQueueMode] = useState<QueueMode>('needs_review');
  const [suiteFilterDraft, setSuiteFilterDraft] = useState('');
  const [caseFilterDraft, setCaseFilterDraft] = useState('');
  const [suiteFilter, setSuiteFilter] = useState('');
  const [caseFilter, setCaseFilter] = useState('');

  const queryArgs = useMemo(
    () => ({
      suite_id: suiteFilter || undefined,
      case_id: caseFilter || undefined,
      requires_review: queueMode === 'needs_review' ? true : undefined,
      is_regression: queueMode === 'regressions' ? true : undefined,
    }),
    [queueMode, suiteFilter, caseFilter]
  );

  const { data, isLoading, isFetching, error, refetch } = useListRunsQuery(queryArgs);

  const runs = data?.runs ?? [];
  const totalCount = data?.total_count ?? runs.length;
  const failedCount = useMemo(() => runs.filter((run) => run.status === 'failed').length, [runs]);

  const applyFilters = () => {
    setSuiteFilter(suiteFilterDraft.trim());
    setCaseFilter(caseFilterDraft.trim());
  };

  const clearFilters = () => {
    setSuiteFilterDraft('');
    setCaseFilterDraft('');
    setSuiteFilter('');
    setCaseFilter('');
  };

  const handleModeChange = (_: React.MouseEvent<HTMLElement>, value: QueueMode | null) => {
    if (value !== null) {
      setQueueMode(value);
    }
  };

  const handleNavigateToRun = (id: string) => {
    navigate(`/admin/evals/runs/${id}`);
  };

  const hasActiveFilters = suiteFilter !== '' || caseFilter !== '';

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      {/* Header row: toggle group + summary chips + refresh */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <ToggleButtonGroup
          value={queueMode}
          exclusive
          onChange={handleModeChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: 2,
            },
          }}
        >
          <ToggleButton value="needs_review">
            <Tooltip title="Runs the AI judge flagged as uncertain">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <RateReviewIcon sx={{ fontSize: 18, mr: 0.75 }} />
                Needs Review
              </Box>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="regressions">
            <Tooltip title="Runs that previously passed but now fail">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingDownIcon sx={{ fontSize: 18, mr: 0.75 }} />
                Regressions
              </Box>
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ flex: 1 }} />

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={`${totalCount} queued`} size="small" variant="outlined" />
          <Chip
            label={`${failedCount} failed`}
            size="small"
            variant="outlined"
            color={failedCount > 0 ? 'error' : 'default'}
          />
          {isFetching && (
            <Typography variant="caption" color="text.secondary">
              Updating...
            </Typography>
          )}
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => refetch()}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Compact filter bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          p: 1,
          borderRadius: 1,
          bgcolor: 'action.hover',
        }}
      >
        <SearchIcon sx={{ fontSize: 20, color: 'text.secondary', ml: 0.5 }} />
        <TextField
          placeholder="Suite ID"
          size="small"
          variant="outlined"
          value={suiteFilterDraft}
          onChange={(event) => setSuiteFilterDraft(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && applyFilters()}
          sx={{
            flex: 1,
            maxWidth: 260,
            '& .MuiInputBase-input': { py: 0.75, fontSize: '0.85rem' },
          }}
        />
        <TextField
          placeholder="Case ID"
          size="small"
          variant="outlined"
          value={caseFilterDraft}
          onChange={(event) => setCaseFilterDraft(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && applyFilters()}
          sx={{
            flex: 1,
            maxWidth: 260,
            '& .MuiInputBase-input': { py: 0.75, fontSize: '0.85rem' },
          }}
        />
        <Chip label="Apply" size="small" onClick={applyFilters} clickable color="primary" />
        {hasActiveFilters && (
          <Tooltip title="Clear filters">
            <IconButton size="small" onClick={clearFilters}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load review queue.
        </Alert>
      )}

      {/* Content: loading skeletons / empty state / run cards */}
      <Stack spacing={1}>
        {isLoading ? (
          <>
            <RunCardSkeleton />
            <RunCardSkeleton />
            <RunCardSkeleton />
          </>
        ) : runs.length === 0 ? (
          <EmptyState mode={queueMode} />
        ) : (
          runs.map((run) => <RunCard key={run.id} run={run} onNavigate={handleNavigateToRun} />)
        )}
      </Stack>
    </Box>
  );
}
